// KPI-28 — Katalog › "Dosyadan içe aktar" modalı (yalnız kpi.manage). Üç adım, tek bileşen
// durum makinesi: yükle (FileDropzone → senkron analyze, ≤60 sn) → gözden geçir
// (CatalogImportReviewTable; satır düzenleme `patchRow`, kapı `computeIssues`) → kataloğa yaz
// (tek tx) → sonuç. Değer yazılmaz, set'ler değişmez. Hata gövdesi `ApiError`'dan okunur;
// sunucu 409 KPI_METRIC_CODE_TAKEN `details.rowId` ile satıra işaret eder.
// Sektör paketleri DR10: gözden geçirmede "Hedef paket" (+ "Yeni paket…"); seçilirse commit gövdesine
// `packageId` girer, sonuçta "N kalem pakete eklendi" + giremeyen satırlar (amber). Paketsiz akış aynı.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../api/client";
import {
  analyzeCatalogImport,
  commitCatalogImport,
  type CatalogImportAnalysisDto,
  type CatalogImportCommitResultDto,
  type CatalogImportRowDto,
} from "../../api/kpi-catalog-import";
import {
  KPI_CATALOG_IMPORT_MAX_BYTES,
  catalogImportRejectReason,
  computeIssues,
  countRows,
  hasWritableRows,
  patchRow,
  type RowPatch,
} from "../../lib/kpi-catalog-import";
import { KPI_CATALOG_IMPORT_COPY } from "../../lib/kpi-catalog-import-copy";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { useKpiPackagesStore } from "../../stores/kpi-packages-store";
import { FileDropzone } from "../common/FileDropzone";
import { CatalogImportReviewTable } from "./catalog-import/CatalogImportReviewTable";
import { CatalogImportTargetPackage } from "./catalog-import/CatalogImportTargetPackage";
import { KpiModal } from "./KpiModal";
import { amberBox, btnGhost, btnPrimary, errorBox, pillStyle } from "./kpi-form-styles";

const ACCEPT =
  ".xlsx,.docx,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf";

type Phase =
  | { kind: "upload"; error: string | null }
  | { kind: "analyzing"; fileName: string }
  | {
      kind: "review";
      analysis: CatalogImportAnalysisDto;
      rows: CatalogImportRowDto[];
      busy: boolean;
      error: string | null;
    }
  | { kind: "done"; result: CatalogImportCommitResultDto };

export interface CatalogImportModalProps {
  open: boolean;
  /** Mevcut katalog kodları (çakışma kapısı) — sayfa `metrics`'ten türetir. */
  existingCodes: ReadonlySet<string>;
  onClose: () => void;
  /** Commit sonrası (katalog yeniden yüklensin + bant). */
  onImported: (result: CatalogImportCommitResultDto) => void;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "KPI_METRIC_CODE_TAKEN") {
      const d = (err.details ?? {}) as { rowId?: string };
      return `${err.message}${d.rowId ? ` (satır ${d.rowId})` : ""}`;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : KPI_CATALOG_IMPORT_COPY.errors.generic;
}

export function CatalogImportModal(props: CatalogImportModalProps): JSX.Element | null {
  const { open, existingCodes, onClose, onImported } = props;
  const copy = KPI_CATALOG_IMPORT_COPY;
  const [phase, setPhase] = useState<Phase>({ kind: "upload", error: null });
  const [targetPackageId, setTargetPackageId] = useState("");
  const packages = useKpiPackagesStore((s) => s.packages);
  const loadPackages = useKpiPackagesStore((s) => s.loadPackages);
  const invalidatePackages = useKpiPackagesStore((s) => s.invalidatePackages);

  useEffect(() => {
    if (open) void loadPackages();
  }, [open, loadPackages]);

  const existingCategoryCodes = useMemo(
    () =>
      new Set(phase.kind === "review" ? phase.analysis.existingCategories.map((c) => c.code) : []),
    [phase],
  );
  const issuesByRow = useMemo(
    () =>
      phase.kind === "review"
        ? computeIssues(phase.rows, existingCodes, existingCategoryCodes)
        : new Map(),
    [phase, existingCodes, existingCategoryCodes],
  );
  const counts = useMemo(
    () => (phase.kind === "review" ? countRows(phase.rows, issuesByRow) : null),
    [phase, issuesByRow],
  );

  const reset = useCallback(() => {
    setPhase({ kind: "upload", error: null });
    setTargetPackageId("");
  }, []);
  const close = useCallback(() => {
    if (phase.kind === "analyzing" || (phase.kind === "review" && phase.busy)) return;
    reset();
    onClose();
  }, [phase, reset, onClose]);

  async function start(file: File): Promise<void> {
    setPhase({ kind: "analyzing", fileName: file.name });
    try {
      const analysis = await analyzeCatalogImport(file);
      setPhase({ kind: "review", analysis, rows: analysis.rows, busy: false, error: null });
    } catch (err) {
      setPhase({ kind: "upload", error: errorMessage(err) });
    }
  }

  function onPatch(rowId: string, patch: RowPatch): void {
    setPhase((p) =>
      p.kind === "review" ? { ...p, rows: patchRow(p.rows, rowId, patch, existingCodes) } : p,
    );
  }

  async function commit(): Promise<void> {
    if (phase.kind !== "review") return;
    setPhase({ ...phase, busy: true, error: null });
    try {
      const result = await commitCatalogImport(
        phase.analysis.fileName,
        phase.rows,
        targetPackageId || null,
      );
      if (targetPackageId) {
        invalidatePackages();
        void loadPackages(true);
      }
      setPhase({ kind: "done", result });
      onImported(result);
    } catch (err) {
      setPhase((p) => (p.kind === "review" ? { ...p, busy: false, error: errorMessage(err) } : p));
    }
  }

  if (!open) return null;

  const stepIndex = phase.kind === "done" ? 2 : phase.kind === "review" ? 1 : 0;
  const canCommit =
    phase.kind === "review" &&
    !phase.busy &&
    counts !== null &&
    counts.issues === 0 &&
    hasWritableRows(phase.rows);

  return (
    <KpiModal
      open={open}
      kicker={copy.kicker}
      title={copy.title}
      description={copy.description}
      width={phase.kind === "review" ? 1180 : 680}
      onClose={close}
      // güvenlik/UX taraması 2026-10-08 (U-M2): dosya çözümlenmiş ama aktarılmamışken kapatma onayı;
      // yükleme/aktarım sürerken `close` zaten kilitli (dirty değil — onay sorulmaz, kapanmaz).
      dirty={phase.kind === "review" && !phase.busy}
      footer={
        phase.kind === "review" ? (
          <>
            <button type="button" style={btnGhost} disabled={phase.busy} onClick={reset}>
              {copy.review.back}
            </button>
            <button
              type="button"
              style={{ ...btnPrimary, opacity: canCommit ? 1 : 0.6 }}
              disabled={!canCommit}
              aria-busy={phase.busy}
              onClick={() => void commit()}
            >
              {phase.busy ? copy.review.committing : copy.review.commit}
            </button>
          </>
        ) : phase.kind === "done" ? (
          <>
            <button type="button" style={btnGhost} onClick={reset}>
              {copy.result.again}
            </button>
            <button type="button" style={btnPrimary} onClick={close}>
              {copy.result.close}
            </button>
          </>
        ) : (
          <>
            {phase.kind === "analyzing" ? (
              <span className="text-xs" style={{ color: "var(--color-text-3)", marginRight: 4 }}>
                {copy.upload.closeLocked}
              </span>
            ) : null}
            <button
              type="button"
              style={{
                ...btnGhost,
                ...(phase.kind === "analyzing" ? { opacity: 0.5, cursor: "not-allowed" } : {}),
              }}
              disabled={phase.kind === "analyzing"}
              aria-disabled={phase.kind === "analyzing" || undefined}
              onClick={close}
            >
              {copy.result.close}
            </button>
          </>
        )
      }
    >
      <ol
        className="flex gap-2 flex-wrap mb-4"
        style={{ listStyle: "none", padding: 0, margin: "0 0 14px" }}
        aria-label="Adımlar"
      >
        {copy.steps.map((s, i) => (
          <li
            key={s}
            aria-current={i === stepIndex ? "step" : undefined}
            style={{
              ...pillStyle,
              ...(i === stepIndex
                ? {}
                : { background: "var(--color-bg)", color: "var(--color-text-3)" }),
            }}
          >
            {(i + 1).toString()}. {s}
          </li>
        ))}
      </ol>

      {phase.kind === "upload" ? (
        <div>
          {phase.error ? (
            <div role="alert" style={{ ...errorBox, marginBottom: 10 }}>
              {copy.upload.failed}: {phase.error}
            </div>
          ) : null}
          <FileDropzone
            accept={ACCEPT}
            multiple={false}
            maxFiles={1}
            maxBytes={KPI_CATALOG_IMPORT_MAX_BYTES}
            label={copy.upload.dropLabel}
            hint={copy.upload.dropHint}
            rejectReason={catalogImportRejectReason}
            onFiles={(files) => {
              const f = files[0];
              if (f) void start(f);
            }}
          />
        </div>
      ) : null}

      {phase.kind === "analyzing" ? (
        <div role="status" aria-live="polite" style={{ padding: "24px 0", textAlign: "center" }}>
          <div style={{ fontWeight: 600, color: "var(--color-text)" }}>{phase.fileName}</div>
          <div style={{ color: "var(--color-text-2)", marginTop: 6 }}>{copy.upload.analyzing}</div>
          <div style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>
            {phase.fileName.toLowerCase().endsWith(".pdf")
              ? copy.upload.analyzingHintPdf
              : copy.upload.analyzingHint}
          </div>
        </div>
      ) : null}

      {phase.kind === "review" && counts ? (
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span style={{ fontWeight: 600, color: "var(--color-text)" }}>
              {phase.analysis.fileName}
            </span>
            <span style={{ color: "var(--color-text-2)", fontSize: "0.85rem" }}>
              {copy.review.summary(counts)}
            </span>
          </div>
          {phase.analysis.warnings.map((w) => (
            <div key={w} style={{ ...amberBox, marginBottom: 8 }}>
              {w}
            </div>
          ))}
          {counts.issues > 0 ? (
            <div style={{ ...amberBox, marginBottom: 8 }}>
              {copy.review.blockers(counts.issues)}
            </div>
          ) : !hasWritableRows(phase.rows) ? (
            <div style={{ ...amberBox, marginBottom: 8 }}>{copy.review.nothingToWrite}</div>
          ) : null}
          {phase.error ? (
            <div role="alert" style={{ ...errorBox, marginBottom: 8 }}>
              {phase.error}
            </div>
          ) : null}
          <CatalogImportTargetPackage
            packages={packages}
            value={targetPackageId}
            onChange={setTargetPackageId}
            onCreated={() => {
              invalidatePackages();
              void loadPackages(true);
            }}
            disabled={phase.busy}
          />
          <CatalogImportReviewTable
            rows={phase.rows}
            issuesByRow={issuesByRow}
            existingCategories={phase.analysis.existingCategories}
            onPatch={onPatch}
            disabled={phase.busy}
          />
        </div>
      ) : null}

      {phase.kind === "done" ? (
        <div role="status" style={{ padding: "8px 0" }}>
          <div style={{ fontWeight: 600, color: "var(--color-success)" }}>{copy.result.title}</div>
          <div style={{ color: "var(--color-text)", marginTop: 4 }}>
            {copy.result.summary(phase.result)}
          </div>
          <CatalogImportPackageResult result={phase.result} />
          <div style={{ color: "var(--color-text-3)", fontSize: "0.8rem", marginTop: 6 }}>
            {copy.result.note}
          </div>
        </div>
      ) : null}
    </KpiModal>
  );
}

/** DR10 sonuç satırları — yalnız hedef paket seçildiyse (sunucu alanları yalnız o zaman döner). */
export function CatalogImportPackageResult({
  result,
}: {
  result: CatalogImportCommitResultDto;
}): JSX.Element | null {
  if (result.packageItemsAdded === undefined) return null;
  const skipped = result.packageSkipped ?? [];
  const T = KPI_PACKAGES_COPY.importTarget;
  return (
    <>
      <div style={{ color: "var(--color-text)", marginTop: 4 }}>
        {T.added(result.packageItemsAdded)}
      </div>
      {skipped.length > 0 ? (
        <div style={{ ...amberBox, marginTop: 6 }}>
          {T.skipped(skipped.length, skipped.join(", "))}
        </div>
      ) : null}
    </>
  );
}
